import { Box, Checkbox, Divider, Radio, Slider, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { LockOpenOutlined, LockOutlined } from "@mui/icons-material";
import { IUserRole } from "src/models";

interface IProps {
  roles: IUserRole[];
  onChange: (roles: IUserRole[]) => void;
  error?: boolean;
  helperText?: string;
}

const PERCENTAGE_STEP = 5;
const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;

const RoleSettings = (props: IProps) => {
  const [lastChangedIndex, setLastChangedIndex] = useState<number>(1);

  function handlePrimaryRoleClicked(roleIndex: number) {
    const currentRoles = props.roles;

    const updatedRoles = currentRoles.map((role, index) => ({
      ...role,
      isPrimaryRole: index === roleIndex ? !currentRoles[roleIndex].isPrimaryRole : false,
    }));

    props.onChange(updatedRoles);
  }

  function getNextRoleIndex(currentIndex: number, skipIndex: number, totalLength: number) {
    let nextIndex = (currentIndex + 1) % totalLength;
    return nextIndex === skipIndex ? (nextIndex + 1) % totalLength : nextIndex;
  }

  function canAdjustRole(role: any, changeAmount: number) {
    if (role.isEngagementLocked) return false;

    if (changeAmount > 0) {
      return role.engagementPercentage <= MAX_PERCENTAGE - PERCENTAGE_STEP;
    } else {
      return role.engagementPercentage >= MIN_PERCENTAGE + PERCENTAGE_STEP;
    }
  }

  function handleEngagementPercentageChange(roleIndex: number, newPercentage: number) {
    const currentRoles = props.roles;
    const updatedRoles = [...currentRoles];

    const lockedPercentageTotal = currentRoles.reduce((sum, role) => {
      return sum + (role.isEngagementLocked ? role.engagementPercentage : 0);
    }, 0);

    const otherUnlockedRoles = currentRoles.filter(
      (role) => !role.isEngagementLocked && role.guid !== currentRoles[roleIndex].guid,
    );

    // If all other roles are locked or the locked percentage is already MAX_PERCENTAGE, do nothing
    if (lockedPercentageTotal >= MAX_PERCENTAGE || (currentRoles.length !== 1 && otherUnlockedRoles.length === 0)) {
      return;
    }

    // Adjust new percentage value if it exceeds the maximum allowed based on locked roles
    const adjustedNewPercentage = Math.min(newPercentage, MAX_PERCENTAGE - lockedPercentageTotal);
    const oldPercentage = currentRoles[roleIndex].engagementPercentage;
    const difference = adjustedNewPercentage - oldPercentage;

    // Update the changed role's percentage
    updatedRoles[roleIndex] = {
      ...updatedRoles[roleIndex],
      engagementPercentage: adjustedNewPercentage,
    };

    const updatedSliderSum = updatedRoles.reduce((sum, role) => sum + role.engagementPercentage, 0);

    // If the value is increasing and the new sum is still less than MAX_PERCENTAGE, just update and return
    if (difference > 0 && updatedSliderSum <= MAX_PERCENTAGE) {
      props.onChange(updatedRoles);
      return;
    }

    let adjustmentTargetIndex = lastChangedIndex;
    let remainingChange = -difference;

    while (Math.abs(remainingChange) >= PERCENTAGE_STEP) {
      adjustmentTargetIndex = getNextRoleIndex(adjustmentTargetIndex, roleIndex, updatedRoles.length);
      const adjustmentTarget = updatedRoles[adjustmentTargetIndex];

      if (canAdjustRole(adjustmentTarget, remainingChange)) {
        const adjustment = remainingChange > 0 ? PERCENTAGE_STEP : -PERCENTAGE_STEP;

        updatedRoles[adjustmentTargetIndex] = {
          ...adjustmentTarget,
          engagementPercentage: adjustmentTarget.engagementPercentage + adjustment,
        };

        remainingChange -= adjustment;
      }

      // Check if we've reached the maximum percentage total
      const currentSum = updatedRoles.reduce((sum, role) => sum + role.engagementPercentage, 0);
      if (currentSum === MAX_PERCENTAGE) break;
    }
    props.onChange(updatedRoles);

    setLastChangedIndex(adjustmentTargetIndex);
  }

  function handleEngagementLockedChange(roleIndex: number, isLocked: boolean) {
    const currentRoles = props.roles;
    const updatedRoles = [...currentRoles];

    updatedRoles[roleIndex] = {
      ...updatedRoles[roleIndex],
      isEngagementLocked: isLocked,
    };

    props.onChange(updatedRoles);
  }

  return (
    <Stack divider={<Divider variant="dashed" />}>
      {props.roles.map((role, index) => (
        <Stack
          key={role.guid}
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.5, sm: 1 }}
          sx={{
            justifyContent: "space-between",
            py: { xs: 0.25, sm: 1 },
            width: "100%",
            alignItems: { xs: "flex-start", sm: "center" }
          }}>
          <Stack direction="row" spacing={0.5} sx={{
            alignItems: "center"
          }}>
            <Radio checked={role.isPrimaryRole} onClick={() => handlePrimaryRoleClicked(index)} />
            <Typography>{`${role.name} at ${role.parentTeam.name}`}</Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: { sm: "33%" }, alignItems: "center" }}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Checkbox
              checked={role.isEngagementLocked}
              icon={<LockOpenOutlined />}
              checkedIcon={<LockOutlined />}
              sx={{ p: 0.25 }}
              onChange={(e) => handleEngagementLockedChange(index, e.target.checked)}
            />

            <Stack spacing={0} sx={{ flexGrow: 1 }}>
              <Slider
                value={role.engagementPercentage}
                onChange={(_e, value) => handleEngagementPercentageChange(index, value as number)}
                disabled={role.isEngagementLocked}
                min={MIN_PERCENTAGE}
                max={MAX_PERCENTAGE}
                step={PERCENTAGE_STEP}
                sx={{ flexGrow: 1 }}
                marks
                valueLabelDisplay="auto"
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body1">as needed</Typography>
                <Typography variant="body1">100%</Typography>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

export { RoleSettings };
